import { Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { RouteTranslationResolver } from './core/services';

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/auth/login',
    pathMatch: 'full'
  },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent)
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./features/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./features/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)
      },
      {
        path: 'activate-account',
        loadComponent: () => import('./features/auth/activate-account/activate-account.component').then(m => m.ActivateAccountComponent)
      }
    ]
  },
  // Legacy route redirect
  {
    path: 'login',
    redirectTo: '/auth/login',
    pathMatch: 'full'
  },
  // 403 landing page (AuthInterceptor redirects here) - full-page, no shell,
  // since the current role's sidebar/menu state may itself be the thing
  // that's broken.
  {
    path: 'access-denied',
    loadComponent: () => import('shared-components').then(m => m.AccessDeniedComponent)
  },
  // Protected routes with layout
  {
    path: '',
    loadComponent: () => import('./layout/app-shell.component').then(m => m.AppShellComponent),
    canActivate: [AuthGuard],
    children: [
      // Main pages
      {
        path: 'dashboard',
        loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent)
      },
      {
        path: 'telephony',
        loadComponent: () => import('./features/telephony/telephony-panel.component').then(m => m.TelephonyPanelComponent)
      },
      {
        path: 'dispatch',
        loadChildren: () => import('./features/dispatcher/dispatcher.routes').then(m => m.dispatcherRoutes)
      },
      {
        path: 'chat',
        resolve: { translations: RouteTranslationResolver },
        loadChildren: () => import('./features/chat/chat.routes').then(m => m.chatRoutes)
      },

      // Management
      {
        path: 'drivers',
        resolve: { translations: RouteTranslationResolver },
        loadChildren: () => import('./features/drivers/drivers.routes').then(m => m.driversRoutes)
      },
      {
        path: 'clients',
        resolve: { translations: RouteTranslationResolver },
        loadChildren: () => import('./features/clients/clients.routes').then(m => m.clientsRoutes)
      },
      {
        path: 'employees',
        resolve: { translations: RouteTranslationResolver },
        loadChildren: () => import('./features/employees/employees.routes').then(m => m.employeesRoutes)
      },
      {
        path: 'cars',
        resolve: { translations: RouteTranslationResolver },
        loadChildren: () => import('./features/cars/cars.routes').then(m => m.carsRoutes)
      },
      {
        path: 'orders',
        resolve: { translations: RouteTranslationResolver },
        loadComponent: () => import('./features/orders/orders-list.component').then(m => m.OrdersListComponent)
      },

      // Financial
      {
        path: 'payments',
        resolve: { translations: RouteTranslationResolver },
        loadChildren: () => import('./features/payments/payments.routes').then(m => m.paymentsRoutes)
      },
      {
        path: 'reports',
        children: [
          {
            path: '',
            redirectTo: 'orders',
            pathMatch: 'full'
          },
          {
            path: ':reportType',
            resolve: { translations: RouteTranslationResolver },
            loadComponent: () => import('./features/reports/reports.component').then(m => m.ReportsComponent)
          }
        ]
      },
      {
        path: 'subscription-status',
        resolve: { translations: RouteTranslationResolver },
        loadChildren: () => import('./features/subscription-status/subscription-status.routes').then(m => m.subscriptionStatusRoutes)
      },

      // Support
      {
        path: 'support',
        resolve: { translations: RouteTranslationResolver },
        loadChildren: () => import('./features/support/support.routes').then(m => m.supportRoutes)
      },

      // Configuration
      {
        path: 'vats',
        loadChildren: () => import('./features/vats/vats.routes').then(m => m.vatsRoutes)
      },
      {
        path: 'nanny-tariffs',
        resolve: { translations: RouteTranslationResolver },
        data: { translationKey: 'nanny_tariffs' },
        loadComponent: () => import('./features/nanny-tariffs/nanny-tariffs.component').then((m) => m.NannyTariffsComponent),
      },
      {
        path: 'roles',
        resolve: { translations: RouteTranslationResolver },
        loadChildren: () => import('./features/roles/roles.routes').then(m => m.rolesRoutes)
      },

      // User Management
      {
        path: 'users',
        resolve: { translations: RouteTranslationResolver },
        loadChildren: () => import('./features/users/users.routes').then(m => m.usersRoutes)
      },

      // Dictionaries
      {
        path: 'dictionaries/car-parameters',
        resolve: { translations: RouteTranslationResolver },
        data: { translationKey: 'parameters' },
        loadComponent: () => import('./features/dictionaries/parameters/parameter-list/parameter-list.component').then(m => m.ParameterListComponent)
      },
      {
        path: 'dictionaries/car-types',
        loadChildren: () => import('./features/dictionaries/car-types/car-types.routes').then(m => m.carTypesRoutes)
      },
      {
        path: 'dictionaries/additionals',
        loadChildren: () => import('./features/additionals/additionals.routes').then(m => m.additionalsRoutes)
      },
      {
        path: 'dictionaries/cancellation-reasons',
        loadChildren: () => import('./features/dictionaries/cancellation-reasons/cancellation-reasons.routes').then(m => m.cancellationReasonsRoutes)
      },
      {
        path: 'dictionaries/rating-reasons',
        resolve: { translations: RouteTranslationResolver },
        data: { translationKey: 'rating-reasons', translationNamespace: 'ratingReasons' },
        loadComponent: () => import('./features/dictionaries/rating-reasons/rating-reason-list/rating-reason-list.component').then(m => m.RatingReasonListComponent)
      },

      {
        path: 'company-profile',
        loadChildren: () => import('./features/company-profile/company-profile.routes').then(m => m.companyProfileRoutes)
      },
      {
        path: 'corporate-phones',
        loadChildren: () => import('./features/corporate-phones/corporate-phones.routes').then(m => m.corporatePhonesRoutes)
      },

      // User
      {
        path: 'user-profile',
        resolve: { translations: RouteTranslationResolver },
        data: { translationKey: 'users' },
        loadComponent: () => import('shared-components').then(m => m.UserViewComponent)
      },
      {
        path: 'documents',
        loadChildren: () => import('./features/documents/documents.routes').then(m => m.documentsRoutes)
      },
      {
        path: 'settings',
        resolve: { translations: RouteTranslationResolver },
        loadComponent: () => import('./features/settings/settings.component').then(m => m.SettingsComponent)
      },
      {
        path: 'websocket-test',
        loadComponent: () => import('./features/testing/websocket-test/websocket-test.component').then(m => m.WebSocketTestComponent)
      },
      {
        path: 'companies',
        loadChildren: () => import('./features/companies/companies.routes').then(m => m.COMPANIES_ROUTES)
      },

      // Dispatcher Panel
      {
        path: 'dispatcher',
        loadChildren: () => import('./features/dispatcher/dispatcher.routes').then(m => m.dispatcherRoutes)
      },
      {
        path: 'setting-keys',
        resolve: { translations: RouteTranslationResolver },
        loadComponent: () => import('./features/setting-keys/setting-keys-list.component').then(m => m.SettingKeysListComponent)
      },

      // Docs Wiki viewer (inside layout - for company panel users).
      // Editing lives only in admin (features/docs-management there) - see
      // shared-docs plan notes. Content is read here via DocsService's
      // public endpoints.
      {
        path: 'docs-wiki',
        loadChildren: () => import('./features/docs-wiki/docs-wiki.routes').then(m => m.docsWikiRoutes)
      },

    ]
  },
  // Wiki viewer (standalone, no layout - used in mobile WebView)
  {
    path: 'wiki/:appType',
    loadChildren: () => import('./features/docs-wiki/docs-wiki.routes').then(m => m.docsWikiRoutes)
  },
  {
    path: '**',
    redirectTo: '/dashboard'
  }
];
